/**
 * Per-template, per-variant feed layouts. Each layout receives:
 *   { site, items, category, AdHeader, AdInline, AdSidebar }
 *
 * The ad slot components are injected so each layout decides where to surface
 * ads. `AdInline` is rendered every N cards.
 */
import React from "react";
import { Link } from "react-router-dom";
import { PUBLIC } from "@/constants/testIds";

function CardImage({ a, ratio = "aspect-[4/3]" }) {
  if (!a.image_url) return null;
  return (
    <img
      src={a.image_url}
      alt=""
      className={`w-full ${ratio} object-cover border`}
      style={{ borderColor: "var(--site-rule)" }}
      loading="lazy"
    />
  );
}

function Kicker({ children }) {
  return <div className="text-[11px] uppercase tracking-widest opacity-60">{children}</div>;
}

/* ============== TRIBUNE ============== */
// v1 — Broadsheet (5-col dense, no hero)
function TribuneBroadsheet({ site, items, AdHeader, AdInline }) {
  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {AdHeader}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5">
        {items.map((a, idx) => (
          <React.Fragment key={a.id}>
            <Link to={`/site/${site.slug}/article/${a.slug}`} data-testid={PUBLIC.articleCard(a.id)} className="block group border-b pb-3" style={{ borderColor: "var(--site-rule)" }}>
              <Kicker>{a.category_slug}</Kicker>
              <div className="display text-base font-bold leading-tight mt-1 group-hover:opacity-80">{a.title}</div>
              <div className="text-[11px] opacity-60 mt-1">{a.source_name}</div>
            </Link>
            {idx === 9 && AdInline}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

// v2 — Magazine (massive hero + 3-up grid)
function TribuneMagazine({ site, items, AdHeader, AdInline }) {
  const lead = items[0];
  const rest = items.slice(1);
  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {AdHeader}
      {lead && (
        <article className="border-b pb-8 mb-8" style={{ borderColor: "var(--site-rule)" }}>
          <Link to={`/site/${site.slug}/article/${lead.slug}`} data-testid={PUBLIC.articleCard(lead.id)} className="block">
            {lead.image_url && <img src={lead.image_url} alt="" className="w-full aspect-[21/9] object-cover" loading="lazy" />}
            <Kicker>{lead.category_slug}</Kicker>
            <h2 className="display text-4xl md:text-6xl font-black tracking-tight mt-3 leading-[1.02]" style={{ color: "var(--site-text)" }}>{lead.title}</h2>
            <p className="mt-4 text-lg opacity-80 leading-relaxed max-w-3xl">{lead.summary}</p>
          </Link>
        </article>
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {rest.map((a, idx) => (
          <React.Fragment key={a.id}>
            <Link to={`/site/${site.slug}/article/${a.slug}`} data-testid={PUBLIC.articleCard(a.id)} className="block group">
              <CardImage a={a} />
              <Kicker>{a.category_slug}</Kicker>
              <div className="display text-xl font-bold leading-tight mt-1 group-hover:opacity-80">{a.title}</div>
              <div className="text-xs opacity-60 mt-2">{a.source_name}</div>
            </Link>
            {idx === 2 && AdInline}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

// v3 — Wire (ticker + 2-column lead)
function TribuneWire({ site, items, AdHeader, AdInline, AdSidebar }) {
  const lead = items[0];
  const rest = items.slice(1);
  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <div className="border-y py-2 px-3 flex flex-wrap gap-x-6 gap-y-1 text-xs mb-6" style={{ borderColor: "var(--site-rule)", fontFamily: "var(--site-display)" }}>
        {items.slice(0, 8).map((a) => (
          <Link key={a.id} to={`/site/${site.slug}/article/${a.slug}`} className="whitespace-nowrap">
            <span className="opacity-60 mr-1.5">●</span>{a.title.slice(0, 70)}
          </Link>
        ))}
      </div>
      {AdHeader}
      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 lg:col-span-9">
          {lead && (
            <article className="border-b pb-6 mb-6 grid grid-cols-1 md:grid-cols-2 gap-6" style={{ borderColor: "var(--site-rule)" }}>
              <Link to={`/site/${site.slug}/article/${lead.slug}`} data-testid={PUBLIC.articleCard(lead.id)}>
                <CardImage a={lead} ratio="aspect-[16/10]" />
              </Link>
              <div>
                <Kicker>{lead.category_slug}</Kicker>
                <Link to={`/site/${site.slug}/article/${lead.slug}`} className="display block text-3xl md:text-4xl font-black tracking-tight mt-2 leading-[1.05]" style={{ color: "var(--site-text)" }}>{lead.title}</Link>
                <p className="mt-3 opacity-80">{lead.summary}</p>
              </div>
            </article>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {rest.map((a, idx) => (
              <React.Fragment key={a.id}>
                <Link to={`/site/${site.slug}/article/${a.slug}`} data-testid={PUBLIC.articleCard(a.id)} className="block group border-b pb-4" style={{ borderColor: "var(--site-rule)" }}>
                  <Kicker>{a.category_slug}</Kicker>
                  <div className="display text-xl font-bold leading-tight mt-1 group-hover:opacity-80">{a.title}</div>
                  <p className="text-sm opacity-80 mt-2">{a.summary?.slice(0, 140)}</p>
                </Link>
                {idx === 3 && AdInline}
              </React.Fragment>
            ))}
          </div>
        </div>
        <aside className="col-span-12 lg:col-span-3 space-y-4">{AdSidebar}</aside>
      </div>
    </div>
  );
}

/* ============== TERMINAL ============== */
// v1 — Mainframe (mono headlines, ticker rail)
function TerminalMainframe({ site, items, AdHeader, AdInline, AdSidebar }) {
  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <div className="mb-6 border py-2 px-3 text-xs flex flex-wrap gap-x-6 gap-y-1" style={{ borderColor: "var(--site-rule)", fontFamily: "var(--site-display)" }}>
        {items.slice(0, 8).map((a) => (
          <Link key={a.id} to={`/site/${site.slug}/article/${a.slug}`} className="whitespace-nowrap">
            <span className="opacity-60 mr-2">▸</span>{a.title.slice(0, 60)}
          </Link>
        ))}
      </div>
      {AdHeader}
      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 lg:col-span-8 divide-y" style={{ borderColor: "var(--site-rule)" }}>
          {items.map((a, idx) => (
            <React.Fragment key={a.id}>
              <article className="py-5">
                <Kicker>[{a.category_slug}] · {a.source_name}</Kicker>
                <Link to={`/site/${site.slug}/article/${a.slug}`} data-testid={PUBLIC.articleCard(a.id)} className="display block text-2xl font-bold mt-1.5" style={{ color: "var(--site-text)" }}>{a.title}</Link>
                <p className="text-sm opacity-80 mt-2">{a.summary}</p>
              </article>
              {idx === 4 && AdInline}
            </React.Fragment>
          ))}
        </div>
        <aside className="col-span-12 lg:col-span-4 space-y-4">
          <div className="border p-4" style={{ borderColor: "var(--site-rule)" }}>
            <Kicker>Movers</Kicker>
            <div className="space-y-1.5 text-xs mt-3" style={{ fontFamily: "var(--site-display)" }}>
              {["AAPL +1.24%", "MSFT +0.78%", "TSLA -2.10%", "NVDA +3.41%", "SPY +0.42%"].map((t) => (
                <div key={t} className="flex justify-between">
                  <span>{t.split(" ")[0]}</span>
                  <span style={{ color: t.includes("-") ? "#dc2626" : "var(--site-accent)" }}>{t.split(" ")[1]}</span>
                </div>
              ))}
            </div>
          </div>
          {AdSidebar}
        </aside>
      </div>
    </div>
  );
}

// v2 — Trading Floor (table + data tiles)
function TerminalTradingFloor({ site, items, AdHeader, AdInline }) {
  // Deterministic per-site demo numbers (avoids react-hooks/purity rule which forbids Math.random in render)
  const seed = (site.id || site.slug || "x").split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  const indices = [
    { label: "S&P", value: 4200 + (seed % 800), change: 0.42 },
    { label: "DOW", value: 33500 + ((seed * 7) % 1500), change: 0.18 },
    { label: "NASDAQ", value: 13800 + ((seed * 11) % 1200), change: 0.91 },
    { label: "VIX", value: 14 + (seed % 8), change: -0.32 },
  ];
  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      {AdHeader}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {indices.map((ix) => (
          <div key={ix.label} className="border p-3" style={{ borderColor: "var(--site-rule)" }}>
            <div className="text-[11px] uppercase tracking-widest opacity-60">{ix.label}</div>
            <div className="display text-2xl font-bold mt-1" style={{ fontFamily: "var(--site-display)" }}>
              {ix.value.toLocaleString()}
            </div>
            <div className="text-xs mt-0.5" style={{ color: ix.change < 0 ? "#dc2626" : "var(--site-accent)" }}>{ix.change > 0 ? "+" : ""}{ix.change.toFixed(2)}%</div>
          </div>
        ))}
      </div>
      <div className="border" style={{ borderColor: "var(--site-rule)" }}>
        <div className="grid grid-cols-[1fr_120px_110px] px-4 py-2 text-[11px] uppercase tracking-widest opacity-60 border-b" style={{ borderColor: "var(--site-rule)" }}>
          <div>Headline</div><div>Category</div><div>Source</div>
        </div>
        <div className="divide-y" style={{ borderColor: "var(--site-rule)" }}>
          {items.map((a, idx) => (
            <React.Fragment key={a.id}>
              <Link to={`/site/${site.slug}/article/${a.slug}`} data-testid={PUBLIC.articleCard(a.id)} className="grid grid-cols-[1fr_120px_110px] gap-3 items-center px-4 py-3 hover:bg-black/5 dark:hover:bg-white/5">
                <div className="display font-semibold truncate" style={{ color: "var(--site-text)" }}>{a.title}</div>
                <div className="text-xs opacity-70" style={{ fontFamily: "var(--site-display)" }}>{a.category_slug}</div>
                <div className="text-xs opacity-60 truncate">{a.source_name}</div>
              </Link>
              {idx === 6 && AdInline}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}

// v3 — Quiet Mono (single column, ultra-minimal)
function TerminalQuietMono({ site, items, AdHeader, AdInline }) {
  return (
    <div className="max-w-2xl mx-auto px-4 py-10">
      {AdHeader}
      <div className="space-y-6">
        {items.map((a, idx) => (
          <React.Fragment key={a.id}>
            <Link to={`/site/${site.slug}/article/${a.slug}`} data-testid={PUBLIC.articleCard(a.id)} className="block group border-b pb-6" style={{ borderColor: "var(--site-rule)" }}>
              <div className="text-[11px] uppercase tracking-widest opacity-60" style={{ fontFamily: "var(--site-display)" }}>{a.category_slug} · {a.source_name}</div>
              <div className="display text-2xl font-bold mt-2 group-hover:opacity-80" style={{ color: "var(--site-text)" }}>{a.title}</div>
              <p className="opacity-80 mt-2">{a.summary}</p>
            </Link>
            {idx === 3 && AdInline}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

/* ============== BRIEF ============== */
// v1 — Editorial (large hero + pull quotes)
function BriefEditorial({ site, items, AdHeader, AdInline }) {
  const lead = items[0];
  const rest = items.slice(1);
  return (
    <div className="max-w-5xl mx-auto px-4 py-12">
      {AdHeader}
      {lead && (
        <article className="mb-16">
          <Link to={`/site/${site.slug}/article/${lead.slug}`} data-testid={PUBLIC.articleCard(lead.id)}>
            {lead.image_url && <img src={lead.image_url} alt="" className="w-full aspect-[21/9] object-cover" loading="lazy" />}
            <Kicker>{lead.category_slug}</Kicker>
            <h2 className="display text-4xl md:text-6xl font-black tracking-tight mt-3 leading-[1.02]" style={{ color: "var(--site-text)" }}>{lead.title}</h2>
            <p className="text-lg opacity-80 mt-5 leading-relaxed max-w-3xl">{lead.summary}</p>
          </Link>
        </article>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
        {rest.map((a, idx) => (
          <React.Fragment key={a.id}>
            <Link to={`/site/${site.slug}/article/${a.slug}`} data-testid={PUBLIC.articleCard(a.id)} className="block group">
              {a.image_url && <img src={a.image_url} alt="" className="w-full aspect-[16/10] object-cover mb-5" loading="lazy" />}
              <Kicker>{a.category_slug}</Kicker>
              <h3 className="display text-2xl font-bold mt-2 leading-tight group-hover:opacity-80">{a.title}</h3>
              <p className="text-sm opacity-80 mt-2">{a.summary}</p>
            </Link>
            {idx === 1 && AdInline}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

// v2 — Curated (asymmetric grid)
function BriefCurated({ site, items, AdHeader, AdInline }) {
  return (
    <div className="max-w-6xl mx-auto px-4 py-10">
      {AdHeader}
      <div className="grid grid-cols-12 gap-6">
        {items.map((a, idx) => {
          // Asymmetric: first item huge, every 5th item wide
          const span =
            idx === 0 ? "col-span-12 md:col-span-8" :
            idx % 5 === 0 ? "col-span-12 md:col-span-12" :
            idx % 3 === 1 ? "col-span-12 md:col-span-4" :
            "col-span-12 md:col-span-6";
          return (
            <React.Fragment key={a.id}>
              <Link to={`/site/${site.slug}/article/${a.slug}`} data-testid={PUBLIC.articleCard(a.id)} className={`${span} block group`}>
                {a.image_url && <img src={a.image_url} alt="" className="w-full aspect-[16/10] object-cover mb-4" loading="lazy" />}
                <Kicker>{a.category_slug}</Kicker>
                <div className={`display ${idx === 0 ? "text-3xl md:text-5xl" : "text-xl"} font-bold mt-2 leading-tight group-hover:opacity-80`}>{a.title}</div>
                {(idx === 0 || idx % 5 === 0) && <p className="opacity-80 mt-3 max-w-2xl">{a.summary}</p>}
              </Link>
              {idx === 4 && <div className="col-span-12">{AdInline}</div>}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

// v3 — Whitespace (single-feature focus)
function BriefWhitespace({ site, items, AdHeader, AdInline }) {
  return (
    <div className="max-w-3xl mx-auto px-4 py-16">
      {AdHeader}
      <div className="space-y-20">
        {items.map((a, idx) => (
          <React.Fragment key={a.id}>
            <article>
              <Link to={`/site/${site.slug}/article/${a.slug}`} data-testid={PUBLIC.articleCard(a.id)}>
                {a.image_url && <img src={a.image_url} alt="" className="w-full aspect-[16/9] object-cover mb-8" loading="lazy" />}
                <Kicker>{a.category_slug}</Kicker>
                <h2 className="display text-3xl md:text-5xl font-black tracking-tight mt-3 leading-[1.05]" style={{ color: "var(--site-text)" }}>{a.title}</h2>
                <p className="opacity-80 mt-5 text-lg leading-relaxed">{a.summary}</p>
              </Link>
            </article>
            {idx === 2 && AdInline}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

const VARIANT_MAP = {
  "tribune::v1": TribuneBroadsheet,
  "tribune::v2": TribuneMagazine,
  "tribune::v3": TribuneWire,
  "terminal::v1": TerminalMainframe,
  "terminal::v2": TerminalTradingFloor,
  "terminal::v3": TerminalQuietMono,
  "brief::v1": BriefEditorial,
  "brief::v2": BriefCurated,
  "brief::v3": BriefWhitespace,
};

export function renderFeed({ site, items, AdHeader, AdInline, AdSidebar }) {
  const key = `${site.template}::${site.variant}`;
  const Layout = VARIANT_MAP[key] || TribuneMagazine;
  return <Layout site={site} items={items} AdHeader={AdHeader} AdInline={AdInline} AdSidebar={AdSidebar} />;
}
